import { COORDINATE_PRECISION, GEOJSON_FEATURE_TYPE } from './constants';
import type { Feature, FeatureType, LineString, Point, Polygon, Position } from './types';

const isCoordinatesEqual = (
    coordinates1: Position,
    coordinates2: Position,
    precision: number = COORDINATE_PRECISION
): boolean => {
    const multiplier = Math.pow(10, precision);
    const roundToPrecision = (num: number): number => Math.round(num * multiplier) / multiplier;

    return (
        roundToPrecision(coordinates1[0]) === roundToPrecision(coordinates2[0]) &&
        roundToPrecision(coordinates1[1]) === roundToPrecision(coordinates2[1])
    );
};

const isPointGeometry = (geometry: Feature['geometry']): geometry is Point =>
    geometry.type === GEOJSON_FEATURE_TYPE.POINT;

const isLineStringGeometry = (geometry: Feature['geometry']): geometry is LineString =>
    geometry.type === GEOJSON_FEATURE_TYPE.LINE_STRING;

const isPolygonGeometry = (geometry: Feature['geometry']): geometry is Polygon =>
    geometry.type === GEOJSON_FEATURE_TYPE.POLYGON;

const isPointFeatureEqual = (feature1: Feature, feature2: Feature): boolean => {
    if (!isPointGeometry(feature1.geometry) || !isPointGeometry(feature2.geometry)) {
        return false;
    }
    const feature1Coordinates = feature1.geometry.coordinates;
    const feature2Coordinates = feature2.geometry.coordinates;

    if (feature1Coordinates.length !== feature2Coordinates.length) {
        return false;
    }

    return isCoordinatesEqual(feature1Coordinates, feature2Coordinates);
};

const isLineStringFeatureEqual = (feature1: Feature, feature2: Feature): boolean => {
    if (!isLineStringGeometry(feature1.geometry) || !isLineStringGeometry(feature2.geometry)) {
        return false;
    }
    const feature1Coordinates = feature1.geometry.coordinates;
    const feature2Coordinates = feature2.geometry.coordinates;

    return feature1Coordinates.every((coord, index) =>
        isCoordinatesEqual(coord, feature2Coordinates[index])
    );
};

const isPolygonFeatureEqual = (feature1: Feature, feature2: Feature): boolean => {
    if (!isPolygonGeometry(feature1.geometry) || !isPolygonGeometry(feature2.geometry)) {
        return false;
    }

    const feature1Coordinates = feature1.geometry.coordinates;
    const feature2Coordinates = feature2.geometry.coordinates;

    if (feature1Coordinates.length !== feature2Coordinates.length) {
        return false;
    }

    for (let i = 0; i < feature1Coordinates.length; i++) {
        if (feature1Coordinates[i].length !== feature2Coordinates[i].length) {
            return false;
        }
    }

    return feature1Coordinates.every((coord, polygonIndex) =>
        coord.every((coord, index) =>
            isCoordinatesEqual(coord, feature2Coordinates[polygonIndex][index])
        )
    );
};

const FEATURE_EQUALITY_MAP = {
    [GEOJSON_FEATURE_TYPE.POINT]: isPointFeatureEqual,
    [GEOJSON_FEATURE_TYPE.LINE_STRING]: isLineStringFeatureEqual,
    [GEOJSON_FEATURE_TYPE.POLYGON]: isPolygonFeatureEqual
};

const isFeatureEqual = (feature1: FeatureType, feature2: FeatureType): boolean => {
    if (feature1.id !== feature2.id) {
        return false;
    }

    return FEATURE_EQUALITY_MAP[feature1.geometry.type](feature1, feature2);
};

const isNumberEqualWithinTolerance = (
    value1: number | undefined,
    value2: number | undefined,
    tolerance: number
): boolean => {
    if (value1 === undefined || value2 === undefined) {
        return value1 === value2;
    }

    return Math.abs(value1 - value2) <= tolerance;
};

export const featureEqualityUtil = {
    isCoordinatesEqual,
    isFeatureEqual,
    isNumberEqualWithinTolerance
};
